/**
 * PageEditorPage
 *
 * Full-page editor for creating and editing landing pages.
 * Replaces the modal-based PageEditorModal with a routable page.
 *
 * Routes:
 * - /pages/new - Create new landing page
 * - /pages/:id/edit - Edit existing landing page
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { FileText, Type, Palette, CheckCircle2, Sun, Moon, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { apiFetch } from '@/client/lib/api';
import { cn } from '@/client/lib/utils';
import { derivePaletteFromPrimary, PRIMARY_COLOR_PRESETS } from '@/client/lib/color-utils';
import { FullPageEditorLayout } from '@/client/components/editors';
import { WizardStep, type WizardStepConfig } from '@/client/components/wizard';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { Button } from '@/client/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import { ThemePicker } from '@/client/components/ui/theme-picker';
import { TypographyControls } from '@/client/components/ui/typography-controls';
import { VisualEffectsControls } from '@/client/components/ui/visual-effects-controls';
import { type PageTheme } from '@/client/lib/themes';

// =============================================================================
// Types
// =============================================================================

interface ProjectOption {
  id: string;
  name: string;
  color: string;
}

type TemplateType = 'product-launch' | 'link-in-bio' | 'event';

// Style options schema
const styleOptionsSchema = z.object({
  backgroundColor: z.string().optional(),
  accentColor: z.string().optional(),
  textColor: z.string().optional(),
  headingColor: z.string().optional(),
  fontFamily: z
    .enum([
      'inter',
      'poppins',
      'merriweather',
      'system',
      'playfair',
      'roboto',
      'montserrat',
      'lora',
    ])
    .optional(),
  maxWidth: z.enum(['narrow', 'medium', 'wide']).optional(),
  padding: z.enum(['compact', 'normal', 'spacious']).optional(),
  // Typography settings
  headingSize: z.number().min(1.5).max(4).optional(),
  bodySize: z.number().min(0.75).max(1.5).optional(),
  lineHeight: z.number().min(1).max(2.5).optional(),
  letterSpacing: z.number().min(-0.05).max(0.2).optional(),
  headingWeight: z.number().min(300).max(900).optional(),
  bodyWeight: z.number().min(300).max(900).optional(),
  // Visual effects settings
  borderRadius: z.number().min(0).max(32).optional(),
  shadowX: z.number().min(-20).max(20).optional(),
  shadowY: z.number().min(-20).max(20).optional(),
  shadowBlur: z.number().min(0).max(50).optional(),
  shadowSpread: z.number().min(-20).max(20).optional(),
  shadowColor: z.string().optional(),
  shadowEnabled: z.boolean().optional(),
  gradientEnabled: z.boolean().optional(),
  gradientType: z.enum(['linear', 'radial']).optional(),
  gradientAngle: z.number().min(0).max(360).optional(),
  gradientStops: z
    .array(z.object({ color: z.string(), position: z.number().min(0).max(100) }))
    .optional(),
  opacity: z.number().min(0).max(100).optional(),
});

// Page editor schema
const pageEditorSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  description: z.string().max(500).optional(),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Lowercase letters, numbers, and hyphens only'),
  projectId: z.string().min(1, 'Project is required'),
  templateType: z.enum(['product-launch', 'link-in-bio', 'event']),
  styleOptions: styleOptionsSchema,
  createShortLink: z.boolean().optional(),
  createQRCode: z.boolean().optional(),
});

type PageEditorInput = z.infer<typeof pageEditorSchema>;
type StyleOptions = z.infer<typeof styleOptionsSchema>;

// Default style options
const DEFAULT_STYLE_OPTIONS: StyleOptions = {
  backgroundColor: '#ffffff',
  accentColor: '#3b82f6',
  textColor: '#374151',
  headingColor: '#111827',
  fontFamily: 'inter',
  maxWidth: 'medium',
  padding: 'normal',
};

// =============================================================================
// Wizard Steps Configuration
// =============================================================================

const getWizardSteps = (): WizardStepConfig[] => [
  { id: 'basics', label: 'Basics', icon: FileText },
  { id: 'content', label: 'Content', icon: Type },
  { id: 'style', label: 'Style', icon: Palette, optional: true },
  { id: 'publish', label: 'Publish', icon: CheckCircle2 },
];

// =============================================================================
// Step Components
// =============================================================================

function BasicsStep({ projects }: { projects: ProjectOption[] }) {
  const form = useFormContext<PageEditorInput>();

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Page Name */}
      <div className="space-y-2">
        <Label htmlFor="name">
          Page Name <span className="text-destructive">*</span>
        </Label>
        <Input id="name" {...form.register('name')} placeholder="My Landing Page" autoFocus />
        {form.formState.errors.name && (
          <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
        )}
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="description">
          Description <span className="text-muted-foreground text-xs">(optional)</span>
        </Label>
        <textarea
          id="description"
          {...form.register('description')}
          rows={2}
          className="w-full px-4 py-2.5 rounded-xl border bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
          placeholder="A brief description of this page"
        />
      </div>

      {/* URL Slug */}
      <div className="space-y-2">
        <Label htmlFor="slug">
          URL Slug <span className="text-destructive">*</span>
        </Label>
        <div className="flex items-center">
          <span className="px-3 py-2.5 rounded-l-xl border border-r-0 bg-muted text-muted-foreground text-sm">
            armenian.link/p/
          </span>
          <Input
            id="slug"
            {...form.register('slug')}
            className="rounded-l-none font-mono text-sm"
            placeholder="my-landing-page"
          />
        </div>
        {form.formState.errors.slug && (
          <p className="text-sm text-destructive">{form.formState.errors.slug.message}</p>
        )}
      </div>

      {/* Project Selection */}
      <div className="space-y-2">
        <Label>
          Project <span className="text-destructive">*</span>
        </Label>
        {projects.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No projects available. Create a project first.
          </p>
        ) : (
          <Select
            value={form.watch('projectId')}
            onValueChange={(value) => form.setValue('projectId', value)}
          >
            <SelectTrigger className="w-full max-w-xs rounded-xl">
              <SelectValue placeholder="Select a project" />
            </SelectTrigger>
            <SelectContent>
              {projects.map((project) => (
                <SelectItem key={project.id} value={project.id}>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: project.color }}
                    />
                    {project.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {form.formState.errors.projectId && (
          <p className="text-sm text-destructive">{form.formState.errors.projectId.message}</p>
        )}
      </div>
    </div>
  );
}

function ContentStep() {
  const form = useFormContext<PageEditorInput>();
  const templateType = form.watch('templateType');

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-8 rounded-xl border-2 border-dashed bg-muted/30 text-center">
        <Type className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
        <h3 className="font-medium mb-2">Content Editor</h3>
        <p className="text-sm text-muted-foreground">
          Template: <span className="font-mono">{templateType}</span>
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          Content editing will be migrated from the modal in the next phase.
        </p>
      </div>
    </div>
  );
}

/**
 * Simplified Style Step - Themes First Approach
 *
 * Reduces the 33 manual style options to a simple flow:
 * 1. Pick a theme (instant application)
 * 2. Optionally adjust primary color + light/dark mode
 * 3. Advanced customization collapsed by default
 */
const STYLE_ADVANCED_STORAGE_KEY = 'armlink-style-advanced-open';

function StyleStep() {
  const form = useFormContext<PageEditorInput>();
  const styleOptions = form.watch('styleOptions');
  const [colorMode, setColorMode] = useState<'light' | 'dark'>('light');
  const [advancedOpen, setAdvancedOpen] = useState(() => {
    // Initialize from localStorage to remember user preference
    try {
      return localStorage.getItem(STYLE_ADVANCED_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Persist advanced panel state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STYLE_ADVANCED_STORAGE_KEY, String(advancedOpen));
    } catch {
      // Ignore storage errors (private browsing, quota exceeded, etc.)
    }
  }, [advancedOpen]);

  // Detect current mode based on background color
  useEffect(() => {
    const bg = styleOptions?.backgroundColor || '#ffffff';
    const isDark = ['#0f172a', '#111827', '#1f2937', '#000000'].some(
      (dark) => bg.toLowerCase() === dark.toLowerCase()
    );
    setColorMode(isDark ? 'dark' : 'light');
  }, [styleOptions?.backgroundColor]);

  // Track when styles have just been updated for visual feedback
  const [justApplied, setJustApplied] = useState<'theme' | 'color' | null>(null);

  // Clear the "just applied" feedback after animation
  useEffect(() => {
    if (justApplied) {
      const timer = setTimeout(() => setJustApplied(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [justApplied]);

  // Apply a theme
  const handleApplyTheme = (theme: PageTheme) => {
    form.setValue('styleOptions.backgroundColor', theme.colors.backgroundColor);
    form.setValue('styleOptions.accentColor', theme.colors.accentColor);
    form.setValue('styleOptions.textColor', theme.colors.textColor);
    form.setValue('styleOptions.headingColor', theme.colors.headingColor);
    form.setValue('styleOptions.fontFamily', theme.typography.fontFamily);
    form.setValue('styleOptions.maxWidth', theme.layout.maxWidth);
    form.setValue('styleOptions.padding', theme.layout.padding);
    setJustApplied('theme');
    toast.success('Theme applied', { duration: 1500 });
  };

  // Apply primary color with derived palette
  const handlePrimaryColorChange = (color: string) => {
    const palette = derivePaletteFromPrimary(color, colorMode);
    form.setValue('styleOptions.backgroundColor', palette.backgroundColor);
    form.setValue('styleOptions.accentColor', palette.accentColor);
    form.setValue('styleOptions.textColor', palette.textColor);
    form.setValue('styleOptions.headingColor', palette.headingColor);
    setJustApplied('color');
  };

  // Toggle light/dark mode
  const handleModeToggle = (mode: 'light' | 'dark') => {
    setColorMode(mode);
    const currentAccent = styleOptions?.accentColor || '#3b82f6';
    const palette = derivePaletteFromPrimary(currentAccent, mode);
    form.setValue('styleOptions.backgroundColor', palette.backgroundColor);
    form.setValue('styleOptions.textColor', palette.textColor);
    form.setValue('styleOptions.headingColor', palette.headingColor);
  };

  const currentAccent = styleOptions?.accentColor || '#3b82f6';

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Theme Picker - Prominent at top */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Palette className="h-5 w-5 text-violet-500" />
          <h3 className="font-semibold">Choose a Theme</h3>
          {justApplied === 'theme' && (
            <span className="ml-2 text-xs font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1 animate-in fade-in slide-in-from-left-2 duration-300">
              <Check className="h-3 w-3" />
              Applied
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Start with a pre-built theme. You can customize colors below.
        </p>
        <div className="p-4 border rounded-xl bg-muted/20">
          <ThemePicker
            currentTheme={{
              backgroundColor: styleOptions?.backgroundColor || '#ffffff',
              accentColor: styleOptions?.accentColor || '#3b82f6',
              textColor: styleOptions?.textColor || '#374151',
              headingColor: styleOptions?.headingColor || '#111827',
              fontFamily: styleOptions?.fontFamily || 'inter',
              maxWidth: styleOptions?.maxWidth || 'medium',
              padding: styleOptions?.padding || 'normal',
            }}
            onApplyTheme={handleApplyTheme}
          />
        </div>
      </div>

      {/* Primary Color + Mode Toggle */}
      <div
        className={cn(
          'p-4 border rounded-xl bg-muted/20 space-y-4 transition-all duration-300',
          justApplied === 'color' && 'ring-2 ring-emerald-500/30'
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div>
              <h4 className="font-medium">Primary Color</h4>
              <p className="text-xs text-muted-foreground">
                Your brand color for buttons and accents
              </p>
            </div>
            {justApplied === 'color' && (
              <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1 animate-in fade-in slide-in-from-left-2 duration-300">
                <Check className="h-3 w-3" />
                Updated
              </span>
            )}
          </div>
          {/* Light/Dark Mode Toggle */}
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
            <Button
              type="button"
              variant={colorMode === 'light' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleModeToggle('light')}
              className="gap-1.5 h-8 px-3"
            >
              <Sun className="h-4 w-4" />
              Light
            </Button>
            <Button
              type="button"
              variant={colorMode === 'dark' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => handleModeToggle('dark')}
              className="gap-1.5 h-8 px-3"
            >
              <Moon className="h-4 w-4" />
              Dark
            </Button>
          </div>
        </div>

        {/* Color Presets */}
        <div className="flex flex-wrap gap-2">
          {PRIMARY_COLOR_PRESETS.map((preset) => (
            <button
              key={preset.value}
              type="button"
              onClick={() => handlePrimaryColorChange(preset.value)}
              className={cn(
                'relative h-10 w-10 rounded-full border-2 transition-all',
                'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2',
                currentAccent.toLowerCase() === preset.value.toLowerCase()
                  ? 'border-foreground ring-2 ring-foreground ring-offset-2'
                  : 'border-transparent'
              )}
              style={{ backgroundColor: preset.value }}
              title={preset.label}
            >
              {currentAccent.toLowerCase() === preset.value.toLowerCase() && (
                <Check className="h-5 w-5 text-white absolute inset-0 m-auto drop-shadow-md" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Customization - Collapsed by default */}
      <div className="border rounded-xl overflow-hidden">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
        >
          <span className="font-medium text-sm">Advanced Customization</span>
          <ChevronDown
            className={cn(
              'h-5 w-5 text-muted-foreground transition-transform duration-200',
              advancedOpen && 'rotate-180'
            )}
          />
        </button>

        {advancedOpen && (
          <div className="border-t p-4 space-y-6 bg-muted/10">
            {/* Typography */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Typography</h4>
              <TypographyControls
                value={{
                  fontFamily: styleOptions?.fontFamily || 'inter',
                  headingSize: styleOptions?.headingSize,
                  bodySize: styleOptions?.bodySize,
                  lineHeight: styleOptions?.lineHeight,
                  letterSpacing: styleOptions?.letterSpacing,
                  headingWeight: styleOptions?.headingWeight,
                  bodyWeight: styleOptions?.bodyWeight,
                }}
                onChange={(settings) => {
                  if (settings.fontFamily)
                    form.setValue('styleOptions.fontFamily', settings.fontFamily);
                  if (settings.headingSize !== undefined)
                    form.setValue('styleOptions.headingSize', settings.headingSize);
                  if (settings.bodySize !== undefined)
                    form.setValue('styleOptions.bodySize', settings.bodySize);
                  if (settings.lineHeight !== undefined)
                    form.setValue('styleOptions.lineHeight', settings.lineHeight);
                  if (settings.letterSpacing !== undefined)
                    form.setValue('styleOptions.letterSpacing', settings.letterSpacing);
                  if (settings.headingWeight !== undefined)
                    form.setValue('styleOptions.headingWeight', settings.headingWeight);
                  if (settings.bodyWeight !== undefined)
                    form.setValue('styleOptions.bodyWeight', settings.bodyWeight);
                }}
              />
            </div>

            {/* Visual Effects */}
            <div className="space-y-3 pt-4 border-t">
              <h4 className="font-medium text-sm">Visual Effects</h4>
              <VisualEffectsControls
                value={{
                  borderRadius: styleOptions?.borderRadius,
                  shadowX: styleOptions?.shadowX,
                  shadowY: styleOptions?.shadowY,
                  shadowBlur: styleOptions?.shadowBlur,
                  shadowSpread: styleOptions?.shadowSpread,
                  shadowColor: styleOptions?.shadowColor,
                  shadowEnabled: styleOptions?.shadowEnabled,
                  gradientEnabled: styleOptions?.gradientEnabled,
                  gradientType: styleOptions?.gradientType,
                  gradientAngle: styleOptions?.gradientAngle,
                  gradientStops: styleOptions?.gradientStops,
                  opacity: styleOptions?.opacity,
                }}
                onChange={(settings) => {
                  if (settings.borderRadius !== undefined)
                    form.setValue('styleOptions.borderRadius', settings.borderRadius);
                  if (settings.shadowX !== undefined)
                    form.setValue('styleOptions.shadowX', settings.shadowX);
                  if (settings.shadowY !== undefined)
                    form.setValue('styleOptions.shadowY', settings.shadowY);
                  if (settings.shadowBlur !== undefined)
                    form.setValue('styleOptions.shadowBlur', settings.shadowBlur);
                  if (settings.shadowSpread !== undefined)
                    form.setValue('styleOptions.shadowSpread', settings.shadowSpread);
                  if (settings.shadowColor !== undefined)
                    form.setValue('styleOptions.shadowColor', settings.shadowColor);
                  if (settings.shadowEnabled !== undefined)
                    form.setValue('styleOptions.shadowEnabled', settings.shadowEnabled);
                  if (settings.gradientEnabled !== undefined)
                    form.setValue('styleOptions.gradientEnabled', settings.gradientEnabled);
                  if (settings.gradientType !== undefined)
                    form.setValue('styleOptions.gradientType', settings.gradientType);
                  if (settings.gradientAngle !== undefined)
                    form.setValue('styleOptions.gradientAngle', settings.gradientAngle);
                  if (settings.gradientStops !== undefined)
                    form.setValue('styleOptions.gradientStops', settings.gradientStops);
                  if (settings.opacity !== undefined)
                    form.setValue('styleOptions.opacity', settings.opacity);
                }}
              />
            </div>

            {/* Layout */}
            <div className="space-y-4 pt-4 border-t">
              <h4 className="font-medium text-sm">Layout</h4>

              {/* Content Width */}
              <div>
                <Label className="text-xs text-muted-foreground">Content Width</Label>
                <div className="flex gap-2 mt-2">
                  {(['narrow', 'medium', 'wide'] as const).map((width) => (
                    <button
                      key={width}
                      type="button"
                      onClick={() => form.setValue('styleOptions.maxWidth', width)}
                      className={cn(
                        'flex-1 px-3 py-2 rounded-lg border text-sm capitalize transition-colors',
                        styleOptions?.maxWidth === width
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50'
                      )}
                    >
                      {width}
                    </button>
                  ))}
                </div>
              </div>

              {/* Spacing */}
              <div>
                <Label className="text-xs text-muted-foreground">Spacing</Label>
                <div className="flex gap-2 mt-2">
                  {(['compact', 'normal', 'spacious'] as const).map((padding) => (
                    <button
                      key={padding}
                      type="button"
                      onClick={() => form.setValue('styleOptions.padding', padding)}
                      className={cn(
                        'flex-1 px-3 py-2 rounded-lg border text-sm capitalize transition-colors',
                        styleOptions?.padding === padding
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50'
                      )}
                    >
                      {padding}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PublishStep({ isEditMode }: { isEditMode: boolean }) {
  const form = useFormContext<PageEditorInput>();
  const name = form.watch('name');
  const slug = form.watch('slug');

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="p-6 rounded-xl border bg-card">
        <h3 className="font-medium mb-4">Ready to Publish</h3>
        <dl className="space-y-3 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Page Name</dt>
            <dd className="font-medium">{name || '(untitled)'}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">URL</dt>
            <dd className="font-mono text-xs">armenian.link/p/{slug || '...'}</dd>
          </div>
        </dl>
      </div>
      <p className="text-sm text-muted-foreground">
        {isEditMode
          ? 'Click "Save" to update your page.'
          : 'Click "Publish" to make your page live.'}
      </p>
    </div>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export function PageEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isEditMode = !!id;
  const templateFromUrl = searchParams.get('template') as TemplateType | null;

  // Fetch projects
  const { data: projectsData, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiFetch('/api/projects');
      if (!response.ok) return { data: [] };
      return response.json();
    },
  });

  const projects: ProjectOption[] = projectsData?.data || [];

  // Fetch existing page for edit mode
  const { data: existingPage, isLoading: isLoadingPage } = useQuery({
    queryKey: ['landing-page', id],
    queryFn: async () => {
      const response = await apiFetch(`/api/landing-pages/${id}`);
      if (!response.ok) throw new Error('Failed to fetch page');
      return response.json();
    },
    enabled: isEditMode,
  });

  // Form setup
  const form = useForm<PageEditorInput>({
    resolver: zodResolver(pageEditorSchema),
    defaultValues: {
      name: '',
      description: '',
      slug: '',
      projectId: '',
      templateType: templateFromUrl || 'product-launch',
      styleOptions: DEFAULT_STYLE_OPTIONS,
    },
  });

  // Load existing page data
  useEffect(() => {
    if (existingPage && isEditMode) {
      form.reset({
        name: existingPage.name || '',
        description: existingPage.description || '',
        slug: existingPage.slug || '',
        projectId: existingPage.projectId || '',
        templateType: existingPage.templateType || 'product-launch',
        styleOptions: existingPage.styleOptions || DEFAULT_STYLE_OPTIONS,
      });
    }
  }, [existingPage, isEditMode, form]);

  // Track dirty state for unsaved changes warning
  const isDirty = form.formState.isDirty;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: PageEditorInput) => {
      const response = await apiFetch('/api/landing-pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          slug: data.slug,
          projectId: data.projectId,
          templateType: data.templateType,
          styleOptions: data.styleOptions,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create page');
      }

      const createdPage = await response.json();

      // Publish the page
      await apiFetch(`/api/landing-pages/${createdPage.id}/publish`, {
        method: 'POST',
      });

      return createdPage;
    },
    onSuccess: () => {
      toast.success('Page published successfully!');
      queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      navigate('/pages');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: PageEditorInput) => {
      const response = await apiFetch(`/api/landing-pages/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          description: data.description,
          slug: data.slug,
          projectId: data.projectId,
          styleOptions: data.styleOptions,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update page');
      }

      return response.json();
    },
    onSuccess: () => {
      toast.success('Page updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['landing-pages'] });
      queryClient.invalidateQueries({ queryKey: ['landing-page', id] });
      navigate('/pages');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Handle complete
  const handleComplete = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error('Please fix the errors before publishing');
      return;
    }

    const data = form.getValues();

    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  // Step validation
  const validateStep = async (stepIndex: number): Promise<boolean> => {
    const step = wizardSteps[stepIndex];
    if (!step) return true;

    if (step.id === 'basics') {
      const result = await form.trigger(['name', 'slug', 'projectId']);
      if (!result) {
        toast.error('Please fill in all required fields');
      }
      return result;
    }

    return true;
  };

  const wizardSteps = getWizardSteps();
  const isLoading = isEditMode && (isLoadingPage || isLoadingProjects);

  return (
    <FormProvider {...form}>
      <FullPageEditorLayout
        title={isEditMode ? 'Edit Page' : 'Create Landing Page'}
        subtitle={
          isEditMode
            ? `Editing ${existingPage?.name || 'page'}`
            : 'Build a landing page for your campaign'
        }
        steps={wizardSteps}
        initialStep={0}
        isEditMode={isEditMode}
        isLoading={isLoading}
        isDirty={isDirty}
        onComplete={handleComplete}
        completeButtonText={isEditMode ? 'Save' : 'Publish'}
        accentColor="violet"
        backPath="/pages"
        hasPreview={true}
        validateStep={validateStep}
      >
        <WizardStep stepId="basics">
          <BasicsStep projects={projects} />
        </WizardStep>

        <WizardStep stepId="content">
          <ContentStep />
        </WizardStep>

        <WizardStep stepId="style">
          <StyleStep />
        </WizardStep>

        <WizardStep stepId="publish">
          <PublishStep isEditMode={isEditMode} />
        </WizardStep>
      </FullPageEditorLayout>
    </FormProvider>
  );
}
